<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ReadMoreIndowStories from '$lib/PageSections/ReadMoreIndowStories/ReadMoreIndowStories.svelte';

	import noiseReduced from './images/noise-reduced.jpg';
	import officeSoundproofing from './images/office-soundproofing.jpg';
	import testimonial from './images/testimonial.jpg';
</script>

<Section>
	<p>
		Office soundproofing is key to concentration and productivity. That&apos;s
		exactly what this marketing firm found out when they moved in right next to
		active train tracks. If you&apos;re looking for office soundproofing ideas
		put to action, read on. Our blog <a
			href="/resources/blog/soundproof-home-office"
			>how to soundproof a home office</a
		> covers converted home workspaces.
	</p>
	<p>
		Marketing firm Foto Crush needed a new home. The historic Olympic Mills
		Building was perfect for their design work for clients like Disney,
		Dreamworks, and Apple. It&apos;s an updated warehouse with lots of large
		windows, so plenty of natural light.
	</p>
	<p>It was perfect, except for the noisy trains right outside.</p>
	<Image src={officeSoundproofing} class="no-bleed" loading="eager" />
	<p>
		&quot;The office, with all of its windows, seemed perfect except I cringed
		at the thought of blasting train horns and worried that the windows would be
		too drafty,&quot; said Cheyenne Pepper, Foto Crush owner. She told the
		property manager that she&apos;d take the space if they took the train noise
		reduction seriously and added office soundproofing to the lease.
	</p>
	<p>
		Property Manager Mike Larkin assumed if one tenant was affected, others were
		too and began searching for office soundproofing ideas. He decided <a
			href="/products/acoustic-window-inserts"
		>
			Indow Acoustic Grade window inserts
		</a>would be the best fit. They can reduce up to 70% of outside noise and
		stop drafts.
	</p>
	<p>
		Best of all, the inserts could be removed and stored if future tenants
		didn&apos;t want them installed for any reason. The inserts are compression
		fit, so require no mounting hardware to install and stay in the window
		frame. That compression tubing is what creates a seal against noise and
		drafts while also creating an air pocket between the insert and window to
		further insulate against noise.
	</p>

	<Bubble backgroundColor="grey-light" class="mb-10">
		{#snippet titleSlot()}
			<p class="text-lg font-bold text-indow-grey-dark! uppercase">
				Office Soundproofing Ideas
			</p>
		{/snippet}
		<p>
			Mike reviewed different office soundproofing methods before determining
			Indow window inserts were the best for their situation. Foto Crush needed
			soundproofing for an open floor layout, which is a more difficult than a
			traditional commercial office space.
		</p>
		<ul>
			<li>
				<p class="mb-0 font-bold">Acoustic Panels</p>
				<p>
					These help minimize sound bouncing around inside. They can be hung
					from the ceiling or added to walls. Because of the number of windows
					in their building, and because the main noise was coming from outside,
					Mike didn't think these would work.
				</p>
			</li>
			<li>
				<p class="mb-0 font-bold">Carpet & upholstered office furniture</p>
				<p>
					These additions also absorb sound, but not enough to mitigate train
					noise.
				</p>
			</li>
			<li>
				<p class="mb-0 font-bold">Sound Masking</p>
				<p>
					Sound masking is popular in open space offices, is not really a form
					of soundproofing. It creates background noise so no individual noise
					is disrupting. This also wouldn't work with the high and low-pitched
					sounds of passing trains.
				</p>
			</li>
			<li>
				<p class="mb-0 font-bold">Acoustic Panels</p>
				<p>
					Most guides pointed to treating windows first, by adding another pane
					or acoustical drapes. Because his tenants wanted natural light, he
					couldn't add drapes or anything that would block those big, gorgeous
					windows.
				</p>
			</li>
			<li>
				<p class="mb-0 font-bold">Window Inserts</p>
				<p>
					Window inserts reduce noise from the outside and absorb noise inside
					the office. They also don't block natural light.
				</p>
			</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonials</h2>
	<Image src={testimonial} class="no-bleed" />
	<p class="text-indow-blue! italic">
		&quot;Acoustic Grade Indow window inserts seemed to be the only solution to
		insulate the train noise. We realized that if we fixed our window problem we
		could lease the space for market rates, instead of leasing far below market.
		The return on the investment has been outstanding and we like happy
		tenants!&quot;
	</p>
	<p>
		– Mike Larkin, Broker & Property Management, Elliot Associates, Portland,
		Oregon
	</p>
	<a href="/resources/case-studies">Read more case studies.</a>
</Section>

<Section>
	<Image
		src={noiseReduced}
		alt="employee happy with sound reduction"
		class="no-bleed"
	/>
	<h2>Train Noise Reduction for a Happy Customer</h2>
	<p class="text-indow-blue! italic">
		&quot;The noise reduction is so impressive we put Indow window inserts into
		the second floor offices for another tenant. They used to have to interrupt
		their conference calls but after the window soundproofing upgrade they
		don&apos;t even notice the train noise. We have budgeted to upgrade the
		entire west side of the Olympic Mills building with Indow window
		inserts.&quot;
	</p>
</Section>

<Section>
	<h2>Office Soundproofing Ideas & Tests</h2>
	<p>
		Not sure if these office soundproofing ideas will work for you? Try these
		action items to test your office space for where and how noise is affecting
		your teammates. Then, you can figure out what actions to take to soundproof
		your office.
	</p>
	<p>
		The Clap Test. Stand in a room, clapping and listening to its echo. You will
		find rooms that are more susceptible to echo and may need more cushioning
		surfaces to absorb sound.
	</p>
	<p>
		The Say Anything test involves our beloved actor John Cusack. Go outside
		with a boom box and walk around the building with music blasting. A
		colleague inside can note what areas in your space seem to have better or
		worse noise entry from outside. It may seem silly, but it helps you
		understand your building envelope better. And hey, maybe your neighbors will
		think a gesture of love is being demonstrated to one of your staff.
	</p>
</Section>

<ReadMoreIndowStories />

<GetAFreeEstimate />
